import React, { useState } from 'react'
import api, { IEmployee, IFloorMapSpace, ISpaceDetails } from '@api'
import { Avatar, Flex, Icon, Toaster, Dialog } from '@ui'
import { Divider, Typography, Button } from '@runeffective/evo-design-system'
import css from './EmployeeList.module.scss'
import defaultImage from '../../../../../../assets/header_desk.svg'
import { get } from 'lodash'
import { ApolloError } from 'apollo-client'
import { UseFloorHook } from '@providers/mapProvider'
import { Tooltip2 } from '@blueprintjs/popover2'
import { Position } from '@blueprintjs/core'
import { SpaceInfo } from '../SpaceInfo'
import { isNeighborhood, isShared } from '@utils'

const EmployeeList = ({
  space,
  assignmentPlanId,
  updateData,
  floorHook,
}: {
  space: IFloorMapSpace
  assignmentPlanId: string
  updateData: (space: ISpaceDetails) => void
  floorHook: UseFloorHook
}) => {
  const [state, setState] = useState<{ isOpen: boolean; employee: any }>({
    isOpen: false,
    employee: {},
  })
  const employees = isShared(space?.usageType)
    ? get(space, 'spaceAssignment.sharedEmployees')
    : get(space, 'spaceAssignment.neighborhood.employees')
  const unseatEmployee = (empl: IEmployee) => {
    if (isNeighborhood(space.usageType)) {
      api.neighborhood
        .removeEmployees(space?.spaceAssignment?.neighborhood?.id as string, [empl.id])
        .then((neighborhood) => {
          updateData({
            ...space,
            spaceAssignment: {
              ...space.spaceAssignment,
              neighborhood: neighborhood,
            },
          } as ISpaceDetails)
          Toaster.success({
            message: 'Employee removed from neighborhood',
          })
        })
    } else {
      api.space
        .seat({
          assignmentPlanId: assignmentPlanId,
          employeeId: empl.id,
          fromSpaceId: space.id,
          moveType: 'move',
          toSpaceId: null,
        })
        .then((response) => {
          if (!response.fromSpace.spaceAssignment.sharedEmployees) floorHook.reloadMap?.()
          updateData({
            ...space,
            spaceAssignment: response.fromSpace.spaceAssignment,
          } as ISpaceDetails)
          Toaster.success({
            message: 'Employee was unseated',
          })
        })
        .catch((error: ApolloError) => {
          Toaster.fail({ message: error.message, error: error })
        })
    }
  }
  return (
    <>
      {employees?.map((employee: IEmployee) => {
        const { id, name, assets, dept } = employee
        return (
          <>
            <Flex key={id} justify="space-between">
              <div
                style={{ cursor: isShared(space.usageType) ? 'pointer' : 'initial' }}
                className="flex items-center text-sm truncate"
                onClick={() =>
                  isShared(space.usageType)
                    ? setState({ ...state, isOpen: true, employee: employee })
                    : () => {}
                }
              >
                <Avatar size={Avatar.Size.XMD} name={name} assets={assets} />
                <Flex
                  style={{ display: 'inline-block' }}
                  className="ml-2 truncate"
                  justify="flex-start"
                  direction="column"
                >
                  <Typography className={css.wrapClass} token="body-strong-01">
                    {name}
                  </Typography>
                  <Typography className={css.empDeptClass} token="body-small-01">
                    {dept?.name}
                  </Typography>
                  <div className={css.seatClass} style={{ marginTop: '2px' }}>
                    <img className={css.deskImg} src={defaultImage} />
                    <div
                      onClick={() =>
                        floorHook.spaceLocate?.({
                          floorId: floorHook.floorId as string,
                          spaceId: space?.id,
                        })
                      }
                    >
                      <Typography className={css.link} token="body-small-01-link">
                        {space.code}
                      </Typography>
                    </div>
                  </div>
                </Flex>
              </div>
              <Tooltip2 content="Unseat" placement={Position.BOTTOM}>
                <Button
                  style={{ marginRight: '20px' }}
                  aria-label="close"
                  layout="borderless"
                  size="small"
                  onClick={() => unseatEmployee(employee)}
                >
                  <Icon name="close" />
                </Button>
              </Tooltip2>
            </Flex>
            <Divider />
          </>
        )
      })}
      <Dialog
        title="Employee details"
        style={{ width: '640px', height: '864px' }}
        isOpen={state.isOpen}
        isCloseButtonShown
        onClose={() => setState({ ...state, isOpen: false })}
        body={
          state.employee && (
            <SpaceInfo
              space={space}
              assignedEmployee={state.employee}
              assignmentPlanId={assignmentPlanId}
              updateData={updateData}
            />
          )
        }
      />
    </>
  )
}

export default EmployeeList
